import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

/**
 * Promo banner admin — upload advertisement image shown at top of app.
 */
export default function PromoAdmin() {
  const [banners, setBanners] = useState([]);
  const [file, setFile] = useState(null);
  const [link, setLink] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/promo-banners").then((r) => setBanners(r.data));
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Choose an image"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("link_url", link);
      fd.append("is_active", "true");
      await api.post("/admin/promo-banner", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Banner uploaded & set as active");
      setFile(null); setLink("");
      load();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  const toggle = async (id) => {
    try {
      await api.patch(`/admin/promo-banners/${id}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Toggle failed");
    }
  };
  const del = async (id) => {
    if (!window.confirm("Delete banner?")) return;
    try {
      await api.delete(`/admin/promo-banners/${id}`);
      toast.success("Banner deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <form onSubmit={upload} className="border border-black/20 p-6 space-y-4" data-testid="admin-promo-form">
        <div className="text-xs uppercase tracking-[0.3em] font-bold">Upload banner image</div>
        <p className="text-xs font-mono text-neutral-500">Recommended: 1600 × 220 px, max 4 MB. Shows at the very top of every page. Click on it takes visitors to your link (optional).</p>
        <Input type="file" accept="image/*" required
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="rounded-none font-mono" data-testid="admin-promo-file" />
        <Input type="url" placeholder="Optional link URL (https://…)"
          value={link} onChange={(e) => setLink(e.target.value)}
          className="rounded-none font-mono" data-testid="admin-promo-link" />
        <Button type="submit" disabled={busy}
          className="rounded-none w-full h-11 bg-[#00E5A0] hover:bg-[#00c98a] text-black text-xs uppercase tracking-[0.2em] font-bold"
          data-testid="admin-promo-submit-btn">
          {busy ? "Uploading…" : "Upload & activate"}
        </Button>
      </form>

      <div>
        <h3 className="text-lg font-black tracking-tight mb-3">All banners</h3>
        <div className="space-y-2" data-testid="admin-promo-list">
          {banners.length === 0 && <div className="border border-black/15 p-4 font-mono text-sm text-neutral-500">No banners yet.</div>}
          {banners.map((b) => (
            <div key={b.id} className={`border p-3 flex items-center gap-3 ${b.is_active ? "border-[#00E5A0] bg-[#F0FDF4]" : "border-black/15"}`}>
              <img src={`${process.env.REACT_APP_BACKEND_URL}${b.image_url}`} alt="" className="w-24 h-14 object-cover border border-black/10" />
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-widest font-bold">{b.is_active ? "ACTIVE" : "Inactive"}</div>
                {b.link_url && <div className="text-xs font-mono text-neutral-500 truncate">{b.link_url}</div>}
              </div>
              <Button onClick={() => toggle(b.id)} variant="outline" size="sm" className="rounded-none border-black/15" data-testid={`admin-promo-toggle-${b.id}`}>
                {b.is_active ? "Deactivate" : "Activate"}
              </Button>
              <Button onClick={() => del(b.id)} variant="outline" size="sm" className="rounded-none border-red-300 text-red-700 hover:bg-red-50" data-testid={`admin-promo-delete-${b.id}`}>
                Delete
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
